import { useChannelTalk } from '@repo/channel-talk';
import { getCookieData } from '@repo/common-utils';
import { MAINTENANCE_SETTING_COOKIE, useWorkingPlatform } from '@repo/platform';
import { PageViewEventLogger, useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { useAppSignInContext, useAuthContext, BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY } from '@repo/user-session';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useRelayEnvironment } from 'react-relay';

import Spinner from '@/components/common/Spinner';
import { TAX_REFUND_ERROR } from '@/components/tax-refund/common/error/error';
import { checkHasHistory } from '@/components/tax-refund/common/graphql/query/checkSearchStatus';
import { PreAccountInfo } from '@/components/tax-refund/lookup/result/apply-possible/PreAccountInfo';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { AUTH_PATHS, COMMON_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import { useCheckHometaxAccount } from '@/lib/hooks/api/mutation/use-check-hometax-account';
import { checkHometaxAvailableTime } from '@/lib/utils/hometax-time';

export default function Page() {
  const router = useRouter();
  const isRedirectStarted = useRef(false);
  const { authService } = useAuthContext();
  const { handleCheckHometaxAccount } = useCheckHometaxAccount();
  const relayEnvironment = useRelayEnvironment();
  const { sendCustomActionEvent } = useUserEventLogger();
  const { isAppUserInitialized, accessType, returnUrl } = useAppSignInContext();
  const { showDialog } = useDialog();
  const { auth } = useAuthContext();
  const { workingPlatform } = useWorkingPlatform();
  const { sendClickEvent } = useUserEventLogger();
  const { openChannelTalk } = useChannelTalk();

  const startLookupRequest = useCallback(async () => {
    const hasHistory = await checkHasHistory(relayEnvironment);
    if (hasHistory) {
      router.replace(COMMON_PATHS.환급조회목록);
      return;
    }
    const isPersonalTritx = sessionStorage.getItem('isPersonalTritx') === 'true';
    if (workingPlatform === 'kbank' || isPersonalTritx) {
      router.replace(`${COMMON_PATHS.간편인증_정보입력}?from=lookup`);
      return;
    }
    handleCheckHometaxAccount({
      variables: {
        corpType: 'INDIS',
        loginType: 'PartnerCert',
        hometaxCookies: auth.user?.hometaxCookies as string,
        tin: auth.user?.tin as string
      },
      successAction: () => {
        sendCustomActionEvent(
          'lookup_request',
          'start',
          {
            businessType: 'indis'
          },
          {
            isUseAirbridge: true,
            isUseGoogleAnalytics: true
          }
        );
        router.replace({ pathname: INDIVIDUAL_PATHS.개인수집및조회, query: { type: 'indis' } });
      },
      errorAction: async (errorType, errorData) => {
        switch (errorType) {
          case TAX_REFUND_ERROR.홈택스주민ID아님:
          case TAX_REFUND_ERROR.홈택스회원아님:
            await showDialog({ title: '홈택스 회원이 아닙니다.', description: '홈택스 회원이 아닌 경우 환급 조회가 불가능합니다.' });
            break;
          case TAX_REFUND_ERROR.기존TIN과불일치:
            await showDialog({ title: '기존 TIN과 불일치', description: '기존 TIN과 불일치하여 환급 조회가 불가능합니다.' });
            break;
          case TAX_REFUND_ERROR.환급완료유저TIN:
            if (errorData) {
              showDialog({
                title: '이미 환급을 신청하셨네요!',
                description: (
                  <div className={'flex flex-col gap-normal-small items-start text-left text-[14px] text-gray-700 leading-5 pb-3'}>
                    <div className={'text-[15px]'}>
                      결과는 신청했던 계정으로만 확인이 가능해요. 비즈넵 환급 서비스에서 아래 로그인 방법을 통해 확인해주세요.
                    </div>
                    <PreAccountInfo loginType={errorData.lastLoginType.toUpperCase()} email={errorData.email} />
                  </div>
                ),
                buttons: {
                  primary: {
                    text: '확인',
                    onClick: () => {
                      sendClickEvent(`redirect_error_sametin-ok`);
                    }
                  },
                  secondary: {
                    text: '문의하기',
                    variant: 'tonal-secondary',
                    onClick: () => {
                      sendClickEvent(`redirect_error_sametin-cx`);
                      openChannelTalk(SUPPORT_BOT_LINK.조회완료.일반);
                    }
                  }
                }
              });
              return;
            }
            await showDialog({ title: '환급 완료한 사용자입니다.', description: '다른 계정으로 환급을 완료한 이력이 있어 조회가 불가능합니다.' });
            break;
          default:
            console.error(`Unknown error: ${errorType}`);
            break;
        }
        router.replace(COMMON_PATHS.환급조회목록);
      }
    });
  }, [auth.user?.hometaxCookies, auth.user?.tin, handleCheckHometaxAccount, relayEnvironment, router, sendCustomActionEvent, showDialog, workingPlatform]);

  useEffect(() => {
    if (!isAppUserInitialized) return;
    if (!accessType) return;
    if (isRedirectStarted.current) return;
    isRedirectStarted.current = true;

    if (accessType === 'autoLoginCode') {
      const maintenanceSetting = getCookieData(MAINTENANCE_SETTING_COOKIE);
      if (!auth.user?.hometaxCookies || !checkHometaxAvailableTime() || (maintenanceSetting?.status && maintenanceSetting?.level === 'hometax')) {
        router.replace(COMMON_PATHS.환급조회목록);
        return;
      }
      startLookupRequest();
      return;
    } else if (workingPlatform === 'kbank') {
      startLookupRequest();
      return;
    }

    const isPersonalTritx = sessionStorage.getItem('isPersonalTritx') === 'true';
    if (isPersonalTritx) {
      startLookupRequest();
      return;
    }

    router.replace(returnUrl ?? COMMON_PATHS.환급랜딩);
  }, [accessType, auth.status, auth.user?.hometaxCookies, authService, isAppUserInitialized, returnUrl, router, startLookupRequest, workingPlatform]);

  useEffect(() => {
    if (router.query?.ext_id) {
      sessionStorage.setItem(BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY, router.query.ext_id as string);
      router.replace(authService?.signPaths.signIn ?? AUTH_PATHS.로그인);
    }
  }, [authService?.signPaths.signIn, router, router.query.ext_id]);

  return (
    <>
      <Head>
        <title>비즈넵 | Redirect</title>
      </Head>
      <div className={`flex min-h-[calc(100vh-2.75rem-6.75rem)] w-full flex-col items-center justify-center`}>
        <Spinner />
      </div>
      <PageViewEventLogger pageName={'redirect'} eventProperty={{ grant_type: router.query.grant_type as string }} />
    </>
  );
}
